import * as React from 'react';
import * as Antd from 'antd';
import { WrappedFormUtils, ValidationRule } from 'antd/lib/form/Form';
import { ButtonSize, ButtonType } from "antd/lib/button";
import bindAllOfThis from "src/utils/BindThisHelper";
import { NameOf } from "src/utils/NameOf";


namespace AntFormItems {
    export interface BaseAntFormItemsProps {
        // Required
        /**
         * The uniqueID of a given element. This will bind to the onSubmit function's return object. Use the NameOf function for best results
         *
         * Basic Example
         * *Model*
         *
         *    class FunClass { firstName: "name here" }
         *
         *  *Property*
         *
         *    ...
         *    uniqueId={NameOf<FunClass>("firstName")}
         *    ...
         *
         * Advanced Example
         * *Model*
         *
         *    class InnerClass { age: 99, isActive: false }
         *    class OuterClass { name: "name", info: InnerClass }
         *
         *  *Property*
         *
         *    ...
         *    uniqueId={ `NameOf<OuterClass>("info").NameOf<InnerClass>("age")` }
         *    ...
         */
        uniqueId: string;

        /** The antd form attribute. Only required when *not* a child of the AntdForm component */
        form?: WrappedFormUtils;

        // Optional
        rules?: ValidationRule[];
        initialValue?: any;
        label?: React.ReactNode;
        visible?: boolean;

        /** React style ClassName declaration for the sub element */
        className?: string;
        /** React style ClassName declaration for the Antd FormItem element */
        formItemClassName?: string;
    }

    export interface GenericProps extends BaseAntFormItemsProps {
    }

    export class Custom extends React.Component<GenericProps> {
        constructor(props) {
            super(props);
            bindAllOfThis(this, Custom.prototype);
        }

        render() {
            let children: any = this.props.children;
            let style: React.CSSProperties = undefined;
            if (this.props.visible === false) {
                style = {
                    display: "none",
                };
            }

            // Debugging and catch statements
            if (this.props.uniqueId == null) {
                console.warn(`${NameOf<InputProps>("uniqueId")} is null. This is a required field`);
                return null;
            }
            if (this.props.form == null) {
                console.warn("Form is null. Please pass in the antd form or be a child of the AntdForm");
                return null;
            }
            if (Array.isArray(children)) {
                console.warn("Form Item children must be a single react component, not an array. Rendering first element");
                children = children[0];
            }

            // Variable null check
            let rules = this.props.rules == null ? undefined : this.props.rules;
            let initialValue = this.props.initialValue == null ? undefined : this.props.initialValue;
            let uniqueId = this.props.uniqueId == null ? undefined : this.props.uniqueId;
            let label = this.props.label == null ? undefined : this.props.label;
            let formItemClassName = this.props.formItemClassName == null ? undefined : this.props.formItemClassName;

            // Create our fancy field decorator function
            let fieldDecorator = this.props.form.getFieldDecorator(uniqueId, {
                initialValue: initialValue,
                rules: rules
            });

            return <Antd.Form.Item label={label} className={formItemClassName} style={style}>
                {fieldDecorator(children)}
            </Antd.Form.Item>;
        }
    }

    export interface ButtonProps {
        /**
         * This is the text to display on the submit button. Can be anything.
         *
         * You can also pass in any number of children, which will override this property
         */
        text?: React.ReactNode;
        size?: ButtonSize;
        type?: ButtonType;
        disabled?: boolean;
        loading?: boolean;
        visible?: boolean;
        onClick?: (e: any) => void;
    }

    export class Button extends React.Component<ButtonProps> {
        constructor(props) {
            super(props);
            bindAllOfThis(this, Button.prototype);
        }

        render() {
            if (this.props.visible === false) {
                return null;
            }

            let size = this.props.size == null ? undefined : this.props.size;
            let type = this.props.type == null ? undefined : this.props.type;
            let disabled = this.props.disabled == null ? undefined : this.props.disabled;
            let loading = this.props.loading == null ? undefined : this.props.loading;
            let onClick = this.props.onClick == null ? undefined : this.props.onClick;

            let displayStuff = this.props.children == null ? this.props.text : this.props.children;

            return <Antd.Button
                size={size}
                type={type}
                disabled={disabled}
                loading={loading}
                onClick={onClick}
            >
                {displayStuff}
            </Antd.Button>;
        }
    }

    export class SubmitButton extends React.Component<ButtonProps> {
        constructor(props) {
            super(props);
            bindAllOfThis(this, SubmitButton.prototype);
        }

        render() {
            if (this.props.visible === false) {
                return null;
            }

            let size = this.props.size == null ? undefined : this.props.size;
            let type = this.props.type == null ? undefined : this.props.type;
            let disabled = this.props.disabled == null ? undefined : this.props.disabled;
            let loading = this.props.loading == null ? undefined : this.props.loading;
            let onClick = this.props.onClick == null ? undefined : this.props.onClick;

            let displayStuff = this.props.children == null ? this.props.text : this.props.children;

            return <Antd.Button
                size={size}
                type={type}
                disabled={disabled}
                loading={loading}
                onClick={onClick}
                htmlType="submit"
            >
                {displayStuff}
            </Antd.Button>;
        }
    }

    export interface InputProps extends BaseAntFormItemsProps {
        // Optional
        placeholder?: string;
        size?: "default" | "large" | "small";
        type?: "text" | "button" | "checkbox" | "color" | "date" | "email" | "file" | "hidden" | "image" | "month" | "number" | "password" | "radio";
        disabled?: boolean;
        /** The value of the component. Will change the value of the underlying element when modified. Overrides initialValue */
        value?: any;

        onClick?: () => void;
        onBlur?: (value: string) => void;
        onChange?: (value: string) => void;
    }

    export class Input extends React.Component<InputProps> {
        constructor(props) {
            super(props);
            bindAllOfThis(this, Input.prototype);
        }

        componentWillReceiveProps(nextProps: InputProps) {
            if (nextProps.value !== this.props.value) {
                this.props.form.setFieldsValue({
                    [nextProps.uniqueId]: nextProps.value
                });
            }
        }

        convertNumberValidationFunc(e: React.FormEvent<HTMLInputElement>) {
            let originalValue = e.currentTarget.value;
            let convertedValue = Number(originalValue);
            if (Number.isNaN(convertedValue)) {
                return originalValue;
            }
            return convertedValue;
        }

        render() {
            if (this.props.visible === false) {
                return null;
            }

            // Debugging and catch statements
            if (this.props.uniqueId == null) {
                console.warn(`${NameOf<InputProps>("uniqueId")} is null. This is a required field`);
                return null;
            }
            if (this.props.form == null) {
                console.warn("Form is null. Please pass in the antd form or be a child of the AntdForm");
                return null;
            }

            // Variable null check
            let rules = this.props.rules == null ? undefined : this.props.rules;
            let value = this.props.value == null ? undefined : this.props.value;
            let initialValue = this.props.initialValue == null ? undefined : this.props.initialValue;
            let uniqueId = this.props.uniqueId == null ? undefined : this.props.uniqueId;
            let label = this.props.label == null ? undefined : this.props.label;
            let size = this.props.size == null ? undefined : this.props.size;
            let type = this.props.type == null ? undefined : this.props.type;
            let placeholder = this.props.placeholder == null ? undefined : this.props.placeholder;
            let disabled = this.props.disabled == null ? undefined : this.props.disabled;
            let onClick = this.props.onClick == null ? undefined : this.props.onClick;
            let onBlur = this.props.onBlur == null ? () => { } : this.props.onBlur;
            let onChange = this.props.onChange == null ? () => { } : this.props.onChange;
            let className = this.props.className == null ? undefined : this.props.className;
            let formItemClassName = this.props.formItemClassName == null ? undefined : this.props.formItemClassName;

            // Special check for Number validation
            let getValueFromEvent = type === "number" || (rules != null && rules.find(x => x.type === "number")) ? this.convertNumberValidationFunc : undefined;

            // Create our fancy field decorator function
            let fieldDecorator = this.props.form.getFieldDecorator(uniqueId, {
                initialValue: value === undefined ? initialValue : value,
                rules: rules,
                getValueFromEvent: getValueFromEvent,
            });

            return <Antd.Form.Item label={label} className={formItemClassName}>
                {fieldDecorator(<Antd.Input className={className}
                    size={size}
                    type={type}
                    placeholder={placeholder}
                    disabled={disabled}
                    onClick={onClick}
                    onBlur={event => onBlur(event.target.value)}
                    onChange={event => onChange(event.target.value)}
                />)}
            </Antd.Form.Item>;
        }
    }
}

/**
 * Obsolete: Please use the `FieldDecorator` component instead. It is located in the `components/common/form` folder
 */
export { AntFormItems };



// WEBPACK FOOTER //
// ./src/utils/formUtil/FormItems.tsx